import { v4 as uuidv4 } from 'uuid';
import { dateOnlyOf, formatDateOnly, orgCalendarDate } from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { ASSESSMENT_TYPES, ReviewTypeKey } from '@/modules/shared/hr-form-engine';
import { REVIEW_STATUS_FIELD } from '../constants/review-core.constants';
import { AssessmentProfileStatus, PmReview, ReviewData, ReviewStatus } from '../types/review-core.types';

export const createReviewData = (reviewType: ReviewTypeKey, patch: Partial<ReviewData> = {}): ReviewData => ({
  ItemId: uuidv4(),
  ReviewType: reviewType,
  ReportingManagerIds: [],
  TransferHistory: [],
  ManagerPeerSelections: [],
  PeerCategoryForms: [],
  PeerSkills: [],
  IsMissedDeadlineNotified: false,
  ...patch,
});

/** Fills the list members the legacy backend always initialised, so callers never null-check them. */
const normalizeReviewData = (raw: Partial<ReviewData>): ReviewData =>
  createReviewData((raw.ReviewType ?? 'peer') as ReviewTypeKey, {
    ...raw,
    ReportingManagerIds: raw.ReportingManagerIds ?? [],
    TransferHistory: raw.TransferHistory ?? [],
    ManagerPeerSelections: raw.ManagerPeerSelections ?? [],
    PeerCategoryForms: raw.PeerCategoryForms ?? [],
    PeerSkills: raw.PeerSkills ?? [],
    ItemId: raw.ItemId ?? uuidv4(),
  });

export const parseReviewData = (json?: string): ReviewData[] => {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as Partial<ReviewData>[]).map(normalizeReviewData) : [];
  } catch {
    return [];
  }
};

export const serializeReviewData = (reviewData: ReviewData[]): string => JSON.stringify(reviewData);

export const findReviewData = (reviewData: ReviewData[], reviewType: ReviewTypeKey) =>
  reviewData.find((entry) => entry.ReviewType === reviewType);

/** Replaces (or appends) the entry of one review type. */
export const upsertReviewData = (reviewData: ReviewData[], next: ReviewData): ReviewData[] =>
  reviewData.some((entry) => entry.ReviewType === next.ReviewType)
    ? reviewData.map((entry) => (entry.ReviewType === next.ReviewType ? next : entry))
    : [...reviewData, next];

/**
 * Deadline that applies to a review type: self uses SelfDeadLine, peers PeersDeadLine, and every manager-driven
 * type the reporting manager deadline (legacy CreatePmReviewDetail / PmReviewService).
 */
const getReviewDeadline = (data: ReviewData | undefined, reviewType: ReviewTypeKey): string | undefined => {
  if (!data) return undefined;
  if (reviewType === 'self') return data.SelfDeadLine ?? undefined;
  if (reviewType === 'peer') return data.PeersDeadLine ?? undefined;
  return data.ReportingManagerDeadLine ?? undefined;
};

export interface ApplicableReviewType {
  reviewType: ReviewTypeKey;
  status: ReviewStatus;
  deadline?: string;
}

/** Review types of this cycle that were actually requested (status other than NotApplicable), in assessment order. */
export const getApplicableReviewTypes = (review: PmReview): ApplicableReviewType[] => {
  const reviewData = parseReviewData(review.ReviewDataJson);
  const assessment = ASSESSMENT_TYPES.find((type) => type.key === review.AssessmentType);
  const order = assessment?.reviewTypes ?? (Object.keys(REVIEW_STATUS_FIELD) as ReviewTypeKey[]);

  return order
    .map((reviewType) => ({
      reviewType,
      status: (review[REVIEW_STATUS_FIELD[reviewType]] ?? 'NotApplicable') as ReviewStatus,
      deadline: getReviewDeadline(findReviewData(reviewData, reviewType), reviewType),
    }))
    .filter((entry) => entry.status !== 'NotApplicable');
};

/** Legacy GetPeerSelectionStatusForManager: the manager's own selection status, falling back to the review-wide one. */
export const getPeerSelectionStatusForManager = (review: PmReview, managerId: string): AssessmentProfileStatus => {
  const fallback = review.PeerSelectedStatus ?? 'Pending';
  if (!managerId) return 'Pending';
  const peerData = findReviewData(parseReviewData(review.ReviewDataJson), 'peer');
  if (!peerData || peerData.ManagerPeerSelections.length === 0) return fallback;
  return peerData.ManagerPeerSelections.find((selection) => selection.ManagerId === managerId)?.SelectionStatus ?? fallback;
};

/** Manager ids a peer request is routed through - the flat, filterable copy kept in PmReview.PeerManagerIds. */
export const collectPeerManagerIds = (reviewData: ReviewData[]): string[] =>
  findReviewData(reviewData, 'peer')?.ReportingManagerIds ?? [];

/**
 * A deadline is a date-only value (kind A): its day is read by its date part and compared with the
 * employee's local today in the organization's zone. The deadline day itself is still on time.
 */
export const isDeadlineMissed = (
  deadline: string | null | undefined,
  now: Date = new Date(),
  timeZone: string = getOrganizationTimeZone()
): boolean => {
  const day = dateOnlyOf(deadline, timeZone);
  if (!day) return false;
  return orgCalendarDate(now.toISOString(), timeZone) > day;
};

/** A date-only value (deadline, ...) as "DD/MM/YYYY" by its date part, or "-" when empty. */
export const formatDate = (value?: string | null): string => (value ? formatDateOnly(value) || '-' : '-');
